import { Box, Typography, Tooltip, Divider } from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import { formatDisplayHours } from "../utils/general";

type HolidaysSummary = {
    bookedHours: number;
    entitlementHours: number;
    carryOverHours: number;
    remainingHours: number;
    dailyPattern: number;
    dailyEquivalent: number;
    weeklyHours: number;
    workingDays: number;
    fullTimeEntitlementHours: number;
    agreementTypeMissing?: boolean;
    noVacationRights?: boolean;
    bookedDaysOffCount?: number;
    settlementHours?: number;
};

type HolidaysSummaryCellProps = {
    monthHolidayHours: number;
    holidaysSummary: HolidaysSummary;
    align?: "start" | "center" | "end";
    size?: "compact" | "default";
};

// Days are shown at the person's daily_equivalent (their real working-day length): 1 decimal, pt-PT comma.
const formatDays = (hours: number, perDay: number) => {
    if (perDay <= 0) return "—";
    const days = (hours / perDay).toFixed(1).replace(".", ",");
    return days;
};

// Whole days without the ",0" tail; fractions keep 1 decimal (22 → "22", 12,83 → "12,8").
const formatDaysValue = (days: number) => {
    const isWholeDays = Math.abs(days - Math.round(days)) < 0.05;
    const formatted = isWholeDays ? String(Math.round(days)) : days.toFixed(1).replace(".", ",");
    return formatted;
};

const formatDailyHours = (hours: number) => {
    const trimmed = parseFloat(hours.toFixed(2));
    const formatted = `${trimmed.toString().replace(".", ",")}h`;
    return formatted;
};

const STANDARD_WEEK_WORKING_DAYS = 5;

// The reader must be able to follow the chain with their own numbers: the law gives DAYS; each vacation
// day is worth weekly ÷ 5 hours (the legal standard week); compressed-week staff then see those hours
// translated into THEIR working days, which are longer.
const buildDaysExplanation = (summary: HolidaysSummary) => {
    const { bookedHours, entitlementHours, dailyPattern, weeklyHours, workingDays, dailyEquivalent } = summary;
    const entitledDays = dailyPattern > 0 ? entitlementHours / dailyPattern : 0;
    const patternHours = formatDailyHours(dailyPattern);
    const equivalentHours = formatDailyHours(dailyEquivalent);
    const hasCompressedWeek = Math.abs(dailyPattern - dailyEquivalent) > 0.01;

    const explanation = (
        <Box sx={{ py: 0.5, maxWidth: 400, lineHeight: 1.55 }}>
            <Typography variant="caption" sx={{ display: "block", fontWeight: 700 }}>
                Como calculamos os seus dias de férias
            </Typography>
            <Divider sx={{ my: 0.75, borderColor: "rgba(255,255,255,0.4)" }} />
            <Typography variant="caption" sx={{ display: "block", fontWeight: 700 }}>
                A regra
            </Typography>
            <Typography variant="caption" sx={{ display: "block" }}>
                • Contam apenas meses completos de trabalho
            </Typography>
            <Typography variant="caption" sx={{ display: "block" }}>
                • 1.º ano: 2 dias por cada mês (máximo 20)
            </Typography>
            <Typography variant="caption" sx={{ display: "block", mb: 1.5 }}>
                • Anos seguintes: 1,83 dias por cada mês (22 dias)
            </Typography>
            <Typography variant="caption" sx={{ display: "block", fontWeight: 700 }}>
                O seu direito
            </Typography>
            <Typography variant="caption" sx={{ display: "block" }}>
                • Este ano: {formatDaysValue(entitledDays)} dias
            </Typography>
            <Typography variant="caption" sx={{ display: "block" }}>
                • Cada dia de férias vale {formatDisplayHours(weeklyHours, 0)} ÷ {STANDARD_WEEK_WORKING_DAYS} dias úteis = {patternHours}
            </Typography>
            <Typography variant="caption" sx={{ display: "block", mb: hasCompressedWeek ? 1.5 : 0 }}>
                • Em horas: {formatDaysValue(entitledDays)} dias × {patternHours} = {formatDisplayHours(entitlementHours, 0)}
            </Typography>
            {hasCompressedWeek ? (
                <>
                    <Typography variant="caption" sx={{ display: "block", fontWeight: 700 }}>
                        Nos seus dias de trabalho
                    </Typography>
                    <Typography variant="caption" sx={{ display: "block" }}>
                        • Um dia seu = {formatDisplayHours(weeklyHours, 0)} ÷ {workingDays} dias = {equivalentHours}
                    </Typography>
                    <Typography variant="caption" sx={{ display: "block" }}>
                        • Direito: {formatDisplayHours(entitlementHours, 0)} ÷ {equivalentHours} = {formatDays(entitlementHours, dailyEquivalent)} dias
                    </Typography>
                    <Typography variant="caption" sx={{ display: "block" }}>
                        • Marcado: {formatDisplayHours(bookedHours, 0)} ÷ {equivalentHours} = {formatDays(bookedHours, dailyEquivalent)} dias
                    </Typography>
                </>
            ) : (
                <Typography variant="caption" sx={{ display: "block" }}>
                    • Marcado: {formatDisplayHours(bookedHours, 0)} ÷ {patternHours} = {formatDays(bookedHours, dailyEquivalent)} dias
                </Typography>
            )}
        </Box>
    );
    return explanation;
};

export const HolidaysSummaryCell = ({
    monthHolidayHours,
    holidaysSummary,
    align = "center",
    size = "compact",
}: HolidaysSummaryCellProps) => {
    const { bookedHours, entitlementHours, carryOverHours, remainingHours, dailyEquivalent } = holidaysSummary;
    const mainVariant = size === "compact" ? "body2" : "body1";

    if (holidaysSummary.agreementTypeMissing) {
        return (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: align, lineHeight: 1.2 }}>
                <Tooltip title="Definir o tipo de contrato em todas as linhas de horas semanais." arrow placement="top">
                    <Typography variant="caption" color="error.main" sx={{ cursor: "help" }}>
                        Tipo de contrato em falta
                    </Typography>
                </Tooltip>
            </Box>
        );
    }

    if (holidaysSummary.noVacationRights) {
        return (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: align, lineHeight: 1.2 }}>
                <Typography variant="caption" color="text.secondary">
                    Não se aplica
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    Férias marcadas este ano: {holidaysSummary.bookedDaysOffCount ?? 0}{" "}
                    {(holidaysSummary.bookedDaysOffCount ?? 0) === 1 ? "dia" : "dias"}
                </Typography>
            </Box>
        );
    }

    const settlementHours = holidaysSummary.settlementHours ?? 0;
    const totalEntitlementHours = entitlementHours + carryOverHours + settlementHours;
    const overbookedHours = bookedHours - totalEntitlementHours;
    const isOverbooked = overbookedHours >= 0.5;
    const canShowDays = dailyEquivalent > 0;

    return (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: align, lineHeight: 1.2 }}>
            {monthHolidayHours > 0 && (
                <Typography variant={mainVariant}>Mês: {formatDisplayHours(monthHolidayHours, 0)}</Typography>
            )}
            <Typography variant={mainVariant}>
                Ano: {formatDisplayHours(bookedHours, 0)} / {formatDisplayHours(entitlementHours, 0)}
            </Typography>
            {canShowDays && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                        {formatDays(bookedHours, dailyEquivalent)} / {formatDays(entitlementHours, dailyEquivalent)} dias
                    </Typography>
                    <Tooltip
                        title={buildDaysExplanation(holidaysSummary)}
                        arrow
                        placement="top"
                        slotProps={{ tooltip: { sx: { maxWidth: 420 } } }}
                    >
                        <InfoOutlined sx={{ fontSize: 14, color: "text.secondary", cursor: "help" }} />
                    </Tooltip>
                </Box>
            )}
            {carryOverHours > 0 && (
                <Typography variant="caption" color="text.secondary">
                    +{formatDisplayHours(carryOverHours, 0)}
                    {canShowDays && ` (${formatDays(carryOverHours, dailyEquivalent)} d)`} de anos anteriores
                </Typography>
            )}
            {settlementHours > 0 && (
                <Typography variant="caption" color="text.secondary">
                    +{formatDisplayHours(settlementHours, 0)}
                    {canShowDays && ` (${formatDays(settlementHours, dailyEquivalent)} d)`} de acerto (contabilidade)
                </Typography>
            )}
            {isOverbooked ? (
                <Typography variant="caption" color="error.main">
                    Excedido em {formatDisplayHours(overbookedHours, 0)}
                    {canShowDays && ` (${formatDays(overbookedHours, dailyEquivalent)} d)`}
                </Typography>
            ) : remainingHours >= 0.5 ? (
                <Typography variant="caption" color="error.main">
                    Faltam marcar {formatDisplayHours(remainingHours, 0)}
                    {canShowDays && ` (${formatDays(remainingHours, dailyEquivalent)} d)`}
                </Typography>
            ) : (
                <Typography variant="caption" color="success.main">
                    Tudo marcado
                </Typography>
            )}
        </Box>
    );
};
